import axios, { isAxiosError } from "axios";
import { buildAxiosAuthenticationHeadersForMultipart } from "../util/build-axios-authentication-headers";
import { dashboardsUrlEnvVar } from "@src/cloud-connect/feature-toggles/feature-toggles";
import { Widget, WidgetPositioning } from "@src/types/dashboards";

/** PUT replaces a single widget's full spec. `id` MUST be absent from
 *  `widget` — it travels in the URL, not the body. Positioning is a
 *  sibling multipart part so the same shape applies to both POST and
 *  PUT. Returns the updated widget. */
export const putUpdateWidgetV2 = async (
  namespace: string,
  dashboardId: string,
  widgetId: string,
  widget: Omit<Widget, "id">,
  positioning: WidgetPositioning,
  imageFile?: File | null,
): Promise<{ data: Widget }> => {
  const url = dashboardsUrlEnvVar();
  const headers = buildAxiosAuthenticationHeadersForMultipart();

  const formData = new FormData();
  formData.append("widget_data", JSON.stringify(widget));
  formData.append("widget_positioning", JSON.stringify(positioning));
  // Three-state body construction for PUT. The FE state semantics match
  // the BE contract, per the dashboards-v2 spec:
  //   imageFile === undefined → Delete clicked → append "delete" (BE drops the existing asset)
  //   imageFile === null      → no-op / no pick → omit part       (BE keeps existing asset)
  //   imageFile is a File     → user picked   → append the file    (BE replaces existing asset)
  // The state→body mapping here is the single source of truth: anything
  // above this line that needs a different intent must remap FIRST.
  if (imageFile instanceof File) {
    formData.append("image", imageFile);
  } else if (imageFile === undefined) {
    // Delete intent: literal "delete" string in the multipart body.
    formData.append("image", "delete");
  }
  // null (keep) → omit part, BE keeps existing asset.

  try {
    const response = await axios.put<Widget>(
      `${url}/api/v2/ns/${namespace}/dashboards/${dashboardId}/widgets/${widgetId}`,
      formData,
      { headers },
    );
    return response;
  } catch (err) {
    if (isAxiosError(err)) {
      console.error("Axios error:", err);
      throw err;
    }
    throw new Error("An unexpected error occurred while updating the widget.");
  }
};
